import { Grid, Group, Paper, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { DateTime } from "luxon";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import LoaderIndicator from "../../components/Loader/LoaderIndicator.tsx";
import MessageContext from "../../context/MessageContext.tsx";
import api, { useEntityTypes } from "../../utils/api.ts";
import AsteroidFieldControls from "./AsteroidFieldControls.tsx";
import EventLog from "./EventLog.tsx";
import MaterialsSummary from "./MaterialsSummary.tsx";
import SystemGrid from "./SystemGrid.tsx";
import XmlImport from "./XmlImport.tsx";
import { parseProspectingLog } from "./parsers.ts";
import { AsteroidFieldData, FieldDeposit, ProspectingEvent } from "./types.ts";
import { formatCgt } from "./utils.ts";
import { extractDeposits, extractGalaxyCoordinates, extractLayout, parseString, transformScansIntoEvents } from "./xml.ts";
import style from "./AsteroidField.module.css";

interface ProspectorProps {
  fieldId: string;
  onDataChange?: () => void;
}

const mergeEvents = (existingEvents: ProspectingEvent[], newEvents: ProspectingEvent[]) => {
  const existing = new Set(existingEvents.map((event) => `${event.date}@(${event.system.x},${event.system.y})`));

  return [...existingEvents, ...newEvents.filter((event) => !existing.has(`${event.date}@(${event.system.x},${event.system.y})`))].sort(
    (a, b) => a.date - b.date,
  );
};

const getLatestEventDate = (events: ProspectingEvent[]): number | null => {
  if (events.length === 0) {
    return null;
  }
  return events.reduce((latest, event) => Math.max(latest, event.date), events[0].date);
};

const notIncludedInExisting = (deposits: FieldDeposit[]) => {
  return (deposit: { system: { x: number; y: number } }) => {
    return !deposits.find((existing) => deposit.system.x === existing.x && deposit.system.y === existing.y);
  };
};

export default function Prospector({ fieldId, onDataChange }: ProspectorProps) {
  const [fieldData, setFieldData] = useState<AsteroidFieldData | null>(null);
  const [loading, setLoading] = useState(true);
  const { error } = useContext(MessageContext);
  const { materials } = useEntityTypes(["materials"]);

  const materialNames = useMemo(() => {
    return materials.reduce<Record<string, string>>((acc, material) => {
      acc[material.id] = material.name;
      return acc;
    }, {});
  }, [materials]);

  const materialIdByName = useMemo(() => {
    return materials.reduce<Record<string, string>>((acc, material) => {
      acc[material.name.toLowerCase()] = material.id;
      return acc;
    }, {});
  }, [materials]);

  const warn = useCallback((title: string, message: string) => {
    notifications.show({
      title,
      message,
      color: "yellow",
      autoClose: 5000,
    });
  }, []);

  const fetchFieldData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/asteroid-fields/${fieldId}/data`);
      setFieldData(response.data as AsteroidFieldData);
    } catch (e: any) {
      error("Failed to load field", e?.response?.data?.error ?? e.message);
    } finally {
      setLoading(false);
    }
  }, [fieldId, error]);

  useEffect(() => {
    fetchFieldData();
  }, [fetchFieldData]);

  const saveAfData = useCallback(
    async (data: Partial<AsteroidFieldData>) => {
      setLoading(true);
      try {
        const response = await api.post(`/asteroid-fields/${fieldId}/data`, data);
        setFieldData(response.data as AsteroidFieldData);
        onDataChange?.();
      } catch (e: any) {
        error("Error", e?.response?.data?.error ?? e.message);
      } finally {
        setLoading(false);
      }
    },
    [fieldId, error, onDataChange],
  );

  const displayDeposits = useMemo(() => {
    if (!fieldData) {
      return [];
    }

    return [
      ...fieldData.deposits,
      ...fieldData.events
        .filter((event) => !!event.deposit)
        .filter(notIncludedInExisting(fieldData.deposits))
        .map((event) => ({
          ...event.system,
          type: event.deposit?.type ?? "",
          size: event.deposit?.size ?? 0,
          stability: event.deposit?.stability,
        }))
        .filter((deposit) => deposit.type !== ""),
    ];
  }, [fieldData]);

  const importXmlData = useCallback(
    (xmlString: string) => {
      if (!xmlString || !fieldData) {
        return;
      }

      try {
        const xml = parseString(xmlString);
        const layout = extractLayout(xml);
        const events = transformScansIntoEvents(xml);
        const deposits = extractDeposits(xml);
        const xmlGalaxy = extractGalaxyCoordinates(xml) ?? events.find((event) => !!event.galaxy)?.galaxy ?? null;
        const xmlLatest = getLatestEventDate(events);

        if (fieldData.layout) {
          if (layout !== fieldData.layout) {
            error("Layout mismatch", "The provided XML appears to be for a different asteroid field.");
            return;
          }
        }

        if (fieldData.fieldGalX !== null && fieldData.fieldGalY !== null && xmlGalaxy) {
          if (xmlGalaxy.x !== fieldData.fieldGalX || xmlGalaxy.y !== fieldData.fieldGalY) {
            warn(
              "Ignored XML upload",
              `This XML is for galaxy (${xmlGalaxy.x}, ${xmlGalaxy.y}) but this field tracks (${fieldData.fieldGalX}, ${fieldData.fieldGalY}).`,
            );
            return;
          }
        }

        if (fieldData.lastFullImportAt && (xmlLatest === null || xmlLatest <= fieldData.lastFullImportAt)) {
          warn(
            "Ignored XML upload",
            `This XML is older than the last full update (${formatCgt(fieldData.lastFullImportAt)}).`,
          );
          return;
        }

        const freshEvents = fieldData.lastFullImportAt
          ? events.filter((event) => event.date > (fieldData.lastFullImportAt ?? 0))
          : events;

        saveAfData({
          layout,
          events: mergeEvents(fieldData.events, freshEvents),
          deposits,
          fieldGalX: xmlGalaxy?.x ?? fieldData.fieldGalX,
          fieldGalY: xmlGalaxy?.y ?? fieldData.fieldGalY,
          lastFullImportAt: xmlLatest ?? fieldData.lastFullImportAt,
        });
      } catch (e: any) {
        error("Could not parse XML", e.message);
      }
    },
    [fieldData, saveAfData, error, warn],
  );

  const readProspectingLog = useCallback(
    (log: string) => {
      if (!fieldData) {
        return;
      }

      const parsedEvents = parseProspectingLog(log);
      if (parsedEvents.length === 0) {
        return;
      }

      const firstGalaxy = parsedEvents.find((event) => !!event.galaxy)?.galaxy ?? null;

      if (fieldData.fieldGalX !== null && fieldData.fieldGalY !== null && firstGalaxy) {
        if (firstGalaxy.x !== fieldData.fieldGalX || firstGalaxy.y !== fieldData.fieldGalY) {
          warn(
            "Ignored event import",
            `Events are for galaxy (${firstGalaxy.x}, ${firstGalaxy.y}) but this field tracks (${fieldData.fieldGalX}, ${fieldData.fieldGalY}).`,
          );
          return;
        }
      }

      let filteredEvents = parsedEvents;
      if (fieldData.fieldGalX !== null && fieldData.fieldGalY !== null) {
        const total = filteredEvents.length;
        filteredEvents = filteredEvents.filter(
          (event) =>
            !event.galaxy || (event.galaxy.x === fieldData.fieldGalX && event.galaxy.y === fieldData.fieldGalY),
        );
        if (filteredEvents.length < total) {
          warn("Partial event import", `${total - filteredEvents.length} event(s) were ignored because they belong to a different field.`);
        }
      }

      if (fieldData.lastFullImportAt) {
        const total = filteredEvents.length;
        filteredEvents = filteredEvents.filter((event) => event.date > fieldData.lastFullImportAt!);
        if (filteredEvents.length < total) {
          warn(
            "Ignored old events",
            `${total - filteredEvents.length} event(s) were ignored because they are older than ${formatCgt(fieldData.lastFullImportAt)}.`,
          );
        }
      }

      if (filteredEvents.length === 0) {
        return;
      }

      const events = filteredEvents.map((event) => {
        if (event.deposit?.type) {
          const typeId = materialIdByName[event.deposit.type.toLowerCase()];
          if (typeId) {
            return {
              ...event,
              deposit: {
                ...event.deposit,
                type: typeId,
              },
            };
          }
          return {
            ...event,
            deposit: undefined,
          };
        }
        return event;
      });

      const deposits = events
        .filter((event) => !!event.deposit && !!event.deposit?.type)
        .filter(notIncludedInExisting(displayDeposits))
        .map((event) => ({
          size: event.deposit?.size ?? 0,
          type: event.deposit?.type ?? "",
          x: event.system.x,
          y: event.system.y,
          stability: "stable",
        }))
        .filter((deposit) => deposit.type !== "");

      saveAfData({
        events: mergeEvents(fieldData.events, events),
        deposits: [...fieldData.deposits, ...deposits],
        fieldGalX: fieldData.fieldGalX ?? firstGalaxy?.x ?? null,
        fieldGalY: fieldData.fieldGalY ?? firstGalaxy?.y ?? null,
      });
    },
    [fieldData, materialIdByName, displayDeposits, saveAfData, warn],
  );

  const resetScans = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.post(`/asteroid-fields/${fieldId}/data/reset`);
      setFieldData(response.data as AsteroidFieldData);
      onDataChange?.();
    } catch (e: any) {
      error("Failed to reset", e?.response?.data?.error ?? e.message);
    } finally {
      setLoading(false);
    }
  }, [fieldId, error, onDataChange]);

  const renameField = useCallback(
    async (name: string) => {
      const trimmed = name.trim();
      if (trimmed === "") {
        return;
      }
      await saveAfData({ name: trimmed });
    },
    [saveAfData],
  );

  const updateMaxDeposits = useCallback(
    async (maxDeposits: number) => {
      setLoading(true);
      try {
        const response = await api.post(`/asteroid-fields/${fieldId}/data/gps`, { maxDeposits });
        setFieldData(response.data as AsteroidFieldData);
        onDataChange?.();
      } catch (e: any) {
        error("Failed to save GPS data", e?.response?.data?.error ?? e.message);
      } finally {
        setLoading(false);
      }
    },
    [fieldId, error, onDataChange],
  );

  const daysAgoLastCheck = useMemo(() => {
    if (!fieldData?.lastGPSCheck) {
      return -1;
    }
    return Math.floor((DateTime.now().toSeconds() - fieldData.lastGPSCheck) / (60 * 60 * 24));
  }, [fieldData?.lastGPSCheck]);

  if (loading) {
    return <LoaderIndicator id="af-loader" message="Loading asteroid field data..." />;
  }

  if (!fieldData) {
    return <Text c="red">Unable to load field data.</Text>;
  }

  const ready = !!fieldData.layout;

  return (
    <Grid align="start" mt="sm">
      <Grid.Col span={{ base: 12, lg: 'content' }}>
        {ready ? (
          <SystemGrid
            layout={fieldData.layout ?? ""}
            events={fieldData.events}
            deposits={displayDeposits}
            materialNames={materialNames}
          />
        ) : (
          <Paper p="sm" withBorder>
            <Text fw={600}>Upload initial XML to initialize the field layout.</Text>
            <div style={{ marginTop: 12 }}>
              <XmlImport doImport={importXmlData} />
            </div>
          </Paper>
        )}
      </Grid.Col>

      <Grid.Col span={{ base: 12, lg: 'auto' }}>
        <Paper p="sm" withBorder>
          <Group justify="space-between" align="start">
            <div>
              <p className={style.fieldName}>{fieldData.name}</p>
              <p className={style.subtle}>
                {(fieldData.maxDeposits ?? false)
                  ? `Last GPS Check: ${
                      daysAgoLastCheck === 0
                        ? "today"
                        : daysAgoLastCheck === 1
                          ? "yesterday"
                          : daysAgoLastCheck > 1
                            ? `${daysAgoLastCheck} days ago`
                            : "unknown"
                    }, ${fieldData.maxDeposits ?? "unknown"} max deposits`
                  : "Never checked"}
                <br />
                Last full XML update: {fieldData.lastFullImportAt ? formatCgt(fieldData.lastFullImportAt) : "never"}
              </p>
            </div>
          </Group>
        </Paper>

        <Paper p="sm" withBorder mt="sm">
          <AsteroidFieldControls
            disabled={loading}
            currentName={fieldData.name}
            renameField={renameField}
            importXml={importXmlData}
            importEvents={readProspectingLog}
            resetScans={resetScans}
            updateMaxDeposits={updateMaxDeposits}
          />
        </Paper>

        <MaterialsSummary
          deposits={displayDeposits}
          totalDeposits={fieldData.maxDeposits}
          materialNames={materialNames}
          lastFullImportAt={fieldData.lastFullImportAt}
        />
        <EventLog events={fieldData.events} materialNames={materialNames} />
      </Grid.Col>
    </Grid>
  );
}
